'use client';

import { ApiApi, Notifications } from '@api';
import { depaginate } from '@data-access';
import { action, makeObservable, observable } from 'mobx';

export class NotificationsStore {
  notifications: Notifications[] = [];

  unreadNotificationsCount = 0;

  showUnreadOnly = false;

  nextPage = null;

  constructor() {
    makeObservable(this, {
      notifications: observable,
      unreadNotificationsCount: observable,
      nextPage: observable,
      addNotifications: action.bound,
      setNotifications: action.bound,
      setUnreadCount: action.bound,
      setNextPage: action.bound,
      toggleUnreadOnly: action.bound,
      updateNotifications: action.bound,
      markAllAsRead: action.bound,
    });
  }

  addNotifications(notifications: Notifications[]) {
    this.notifications = [...this.notifications, ...notifications];
  }

  setNotifications(notifications: Notifications[]) {
    if (this?.notifications) {
      this.notifications = notifications;
    }
  }

  setUnreadCount(count: number) {
    this.unreadNotificationsCount = count;
  }

  setNextPage(page: number) {
    this.nextPage = page;
  }

  toggleUnreadOnly() {
    this.showUnreadOnly = !this.showUnreadOnly;
  }

  async markAllAsRead(api: ApiApi) {
    try {
      const unreadNotifications = await depaginate(page => api.apiNotificationsList({ unread: true, page }));
      if (unreadNotifications.length > 0) {
        unreadNotifications.forEach(async n => {
          await api.apiNotificationsMarkAsReadCreate({
            id: n.id,
          });
        });
      }
      this.setUnreadCount(0);
      if (this.showUnreadOnly) {
        this.setNotifications([]);
        this.setNextPage(null);
      } else {
        // mark all as read locally
        this.notifications = this.notifications.map(n => ({ ...n, unread: false }));
      }
    } catch (error) {
      console.log(error);
    }
  }

  async updateNotifications(api: ApiApi) {
    const unreadResponse = await api.apiNotificationsList({ unread: true });
    // update number of unread notifications (count indicator)
    this.setUnreadCount(unreadResponse.count);
    if (this.showUnreadOnly) {
      this.setNotifications(unreadResponse.results);
      this.setNextPage(unreadResponse?.next ? parseInt(unreadResponse?.next.split('page=')[1], 10) : null);
    } else {
      // get all notifications
      const response = await api.apiNotificationsList();
      this.setNotifications(response.results);

      this.setNextPage(response?.next ? parseInt(response?.next.split('page=')[1], 10) : null);
    }
  }
}

export const notificationsStore = new NotificationsStore();
